import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import { credentialRefused } from '../api/client'
import { ErrorNotice, Pending } from '../ui'
import { useSession } from './session'

/**
 * The gate in front of every screen that needs a person.
 *
 * A refused credential sends somebody back to sign in rather than leaving them
 * on a screen full of 401s: the API is the authority on whether a person may
 * be here, and the browser's only job is to believe it the first time.
 *
 * **Only a refusal.** This gate used to treat any failure of `/session/me` as
 * one, and the query does not retry, so a single 500, a 502 from the proxy
 * while the container comes back, or a link that dropped discarded whatever
 * the person was part-way through and told them their session had expired.
 * Their credential was still good -- signing in again worked immediately,
 * which is the tell -- and the sentence sent them looking for a credential
 * problem that did not exist. This deployment restarts on every run of the
 * deploy job and a site node is on a link that drops, so it was not rare.
 *
 * What it does instead: a failed refetch behind a session this browser already
 * holds leaves the person on their screen, because the answer is still good
 * enough to render and the screens beneath report their own failures. A first
 * read that fails offers a retry, which is what `ErrorNotice` is for and what
 * every other screen in this front end already does.
 *
 * **The way back is carried.** Somebody sent to sign in from a screen is sent
 * back to that screen afterwards, through the `return_to` the sign-in page
 * already honours. The welcome mail's button opens the project it is about,
 * and before this the person who signed in from it landed on the start screen
 * and had to find the project again.
 */
export function RequireSession({ children }: { children: ReactNode }) {
  const { credentials, me, refused } = useSession()
  const location = useLocation()
  const here = location.pathname + location.search + location.hash
  const door = signInFrom(here)
  // The provider drops a refused credential as soon as it sees the refusal, so
  // by the time this renders again there is no credential to test. What is
  // left is `refused`, and it is read first: somebody whose sign-in was just
  // refused goes to sign in, where that is explained, and never to the
  // welcome page a visitor with nothing in hand is given.
  if (!credentials) return <Navigate to={refused ? door : firstDoor(here)} replace />
  if (credentialRefused(me.error)) return <Navigate to={door} replace />
  if (me.data) return <>{children}</>
  if (me.isPending) return <Pending />
  if (me.error) return <ErrorNotice error={me.error} retry={() => void me.refetch()} />
  return <>{children}</>
}

/**
 * The sign-in address that comes back to `here`.
 *
 * The start screen needs no `return_to`, because it is where a sign-in lands
 * anyway, and whatever query it carries is not carried either: that is where
 * the server's own markers arrive (`?signin_failed=1`), and the sign-in page
 * has always been reached from there with a clean address. Anything else is
 * carried whole, query and fragment included. The sign-in page decides whether
 * to believe it (`landingFor`), so nothing here filters it twice.
 */
/**
 * Where somebody with no sign-in at all is sent from `here`.
 *
 * The bare start address is the front door of the site, and the owner chose on
 * 27 October 2026 that a visitor arriving there with nothing in hand meets the
 * welcome page, which carries its own way on to sign in. Anywhere else, and
 * the start address with anything after it, goes to sign in as before: a
 * deep link is somebody who means to sign in, and the server's own markers
 * (`?signin_failed=1`) belong to the sign-in page. A credential the instance
 * refused is somebody who was signed in, and goes to sign in too.
 */
export function firstDoor(here: string): string {
  if (here === '' || here === '/') return '/welcome'
  return signInFrom(here)
}

export function signInFrom(here: string): string {
  const path = here.split(/[?#]/, 1)[0]
  if (path === '' || path === '/') return '/signin'
  return `/signin?return_to=${encodeURIComponent(here)}`
}
